import { useCallback, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import { useCopilot } from '../hooks/useCopilot'
import { AutoGrowTextarea } from './AutoGrowTextarea'

/** Accepted input kinds, shown as chips instead of a grey sentence. Word (.doc/.docx) is read by the
 *  copilot service since T1-4075; the dropzone itself filters nothing. */
export const FORMATS = [
  { key: 'pdf', icon: 'fa-file-pdf' },
  { key: 'image', icon: 'fa-image' },
  { key: 'email', icon: 'fa-envelope' },
  { key: 'sheet', icon: 'fa-table' },
  { key: 'word', icon: 'fa-file-word' },
] as const

export function EmptyState() {
  const { t } = useTranslation()
  const { startRun } = useCopilot()
  const [files, setFiles] = useState<File[]>([])
  const [instruction, setInstruction] = useState('')

  const onDrop = useCallback((accepted: File[]) => setFiles((prev) => [...prev, ...accepted]), [])
  const { getRootProps, getInputProps } = useDropzone({ onDrop })

  const onPaste = useCallback((e: React.ClipboardEvent) => {
    const pasted = Array.from(e.clipboardData.files)
    if (pasted.length) setFiles((prev) => [...prev, ...pasted])
  }, [])

  const canStart = files.length > 0 || instruction.trim().length > 0

  return (
    <section className="copilot-empty" onPaste={onPaste}>
      <p className="copilot-empty__eyebrow">{t('copilot.empty.eyebrow')}</p>
      <h2 className="copilot-empty__headline">{t('copilot.empty.headline')}</h2>
      <p className="copilot-empty__lead">{t('copilot.empty.hint')}</p>

      <div {...getRootProps({ className: 'copilot-dropzone' })}>
        <input {...getInputProps()} />
        <div className="copilot-dropzone__icon" aria-hidden="true"><i className="fa fa-file-upload" /></div>
        <p className="copilot-dropzone__hint">{t('copilot.empty.dropHint')}</p>
        <ul className="copilot-dropzone__formats">
          {FORMATS.map((f) => (
            <li key={f.key}>
              <i className={`fa ${f.icon}`} aria-hidden="true" />
              {t(`copilot.empty.format.${f.key}`)}
            </li>
          ))}
        </ul>
        <small className="copilot-dropzone__limits" data-testid="copilot-limits">
          {t('copilot.empty.limits')}
        </small>
      </div>

      {files.length > 0 && (
        <ul className="copilot-filelist">
          {files.map((f) => (
            <li key={`${f.name}-${f.size}`}>
              <span className="copilot-filelist__icon" aria-hidden="true"><i className="fa fa-file" /></span>
              {f.name}
              <span className="copilot-filelist__size">{Math.max(1, Math.round(f.size / 1024))} KB</span>
            </li>
          ))}
        </ul>
      )}

      <div className="copilot-field">
        <label className="copilot-field__label" htmlFor="copilot-instruction">
          {t('copilot.empty.instructionLabel')}
        </label>
        <AutoGrowTextarea
          id="copilot-instruction"
          data-testid="copilot-instruction"
          className="form-control copilot-empty__instruction"
          minRows={4}
          maxRows={12}
          placeholder={t('copilot.empty.instructionPlaceholder')}
          value={instruction}
          onChange={(e) => setInstruction(e.target.value)}
        />
      </div>

      <div className="copilot-startrow">
        <button
          type="button"
          className="btn btn-primary copilot-start"
          data-testid="copilot-start"
          disabled={!canStart}
          onClick={() => startRun(files, instruction.trim())}
        >
          {t('copilot.empty.start')}
          <i className="fa fa-arrow-right" aria-hidden="true" />
        </button>
        <span className="copilot-startrow__hint" data-testid="copilot-start-hint">
          <i className="fa fa-info-circle" aria-hidden="true" />
          {t('copilot.empty.startHint')}
        </span>
      </div>
    </section>
  )
}
